import styles from './FJSourcePreviewItem.module.less';
import { memo, useState, useRef, useCallback } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import FJSourcePreviewItemBtu from './FJSourcePreviewItemBtu';
import FJSourceReviewItemStatus from './FJSourceReviewItemStatus';
import FJVideoPreviewItem from './FJVideoPreviewItem';
import FJConstant from '../../../constants/FJConstant';
import { useMemo } from 'react';
import FJAppConstant from '../../../constants/FJAppConstant';
import FJImagePreviewItem from './FJImagePreviewItem';
import FJAIToolUtil from '../../../util/FJAIToolUtil.js';
import FJCommunityUtil from '../../../util/FJCommunityUtil.js';

const FJSourcePreviewItem = memo(props => {
    const {
        item,
        appType,

        selected = false,
        isShowSelected = false,
        hideSelect = false,
        hideDelete = false,
        hideCompare = true,
        hideDownload = false,
        hideEdit = true,
        isShowPrivacyStatus = true,
        isTransparentBG = false,
        isShowAddToMediaButton = false,
        isApplyButton = false,
        isShowAddToEditPageButton = false,
        onPrivacySettingChange,
        onAddToMedia,
        onApplyMedia,
        onSelected,
        onDelete,
        onDownload,
        reGenerate,
        onClick,
        onEdit,
        onReload,
        onAddToEditPage,
        retryLoadingIds = [],
        isShowGenerating = false,
        creationListLength = 0,
        isCreation = false,
    } = props;

    const {
        imgUrl,
        id,
        width,
        height,
        isLoading,
        isReload,
        originalUrl,
        videoUrl,
        status,
        generationType,
        privacyStatus,
        shareKey,
    } = item;

    const [sourceLoadStatus, setSourceLoadStatus] = useState(FJConstant.LOADING);
    const [sourceOriginLoadStatus, setSourceOriginLoadStatus] = useState(FJConstant.LOADING);
    const [isHover, setIsHover] = useState(false);
    const [isComparison, setIsComparison] = useState(false);
    const videoPreviewItemRef = useRef(null);
    const imagePreviewItemRef = useRef(null);

    const _onClick = useCallback(
        e => {
            e.stopPropagation();
            if (
                sourceLoadStatus === FJConstant.LOADING ||
                sourceLoadStatus === FJConstant.ERROR ||
                isLoading ||
                isReload
            )
                return;
            onClick && onClick(item);
        },
        [sourceLoadStatus, isLoading, isReload, onClick, item],
    );

    // FJSourceReviewItemStatus 状态
    const sourceStatus = useMemo(() => {
        if (status === 'progress') {
            return FJConstant.PROGRESS;
        }

        if (status === 'failed') {
            return FJConstant.ERROR;
        }

        if (status === 'queued') {
            return FJConstant.WAITING;
        }

        if (isReload) {
            return FJConstant.RETRY;
        }

        if (isLoading) {
            return FJConstant.LOADING;
        }

        // 资源加载失败状态和 isReload  status === 'failed' 区分开
        if (sourceLoadStatus === FJConstant.ERROR) {
            return FJConstant.SOURCE_LOADING_ERROR;
        }

        if (sourceLoadStatus === FJConstant.LOADING) {
            return FJConstant.LOADING;
        }

        return sourceLoadStatus;
    }, [status, sourceLoadStatus, isReload, isLoading]);

    // 状态界面需要的特殊数据
    const { errorCode, model, generationType: itemGenType, originalUrl: itemOrgUrl } = item;
    const statusData = useMemo(() => {
        const baseData = {
            errorCode: errorCode,
            model: model,
        };
        if (appType === FJAppConstant.AI_VIDEO_GENERATOR) {
            return {
                ...baseData,
                generationType: itemGenType,
                originalUrl: itemOrgUrl,
            };
        }
        return baseData;
    }, [errorCode, model, appType, itemGenType, itemOrgUrl]);

    const _onReloadOrigin = useCallback(() => {
        videoPreviewItemRef.current?.sourceOriginReload();
    }, []);

    const _reGenerate = useCallback(() => {
        reGenerate(id);
    }, [reGenerate, id]);

    const _onSourceReload = useCallback(() => {
        if (videoUrl) {
            videoPreviewItemRef.current?.sourceReload();
        } else {
            imagePreviewItemRef.current?.sourceReload();
        }
        onReload && onReload(id);
    }, [videoUrl, onReload, id]);

    const _onApplyMedia = useCallback(
        e => {
            e.stopPropagation();
            let options;
            if (width && height) {
                options = {
                    size: { width: width, height: height },
                };
            }
            const isVideo = FJAIToolUtil.getVideoAppTypeList().includes(appType);
            if (isVideo) {
                onApplyMedia && onApplyMedia(videoUrl);
            } else {
                onApplyMedia && onApplyMedia(imgUrl, options);
            }
        },
        [width, height, appType, onApplyMedia, videoUrl, imgUrl],
    );

    // 添加到编辑页的媒体库
    const _onAddToMediaLibrary = useCallback(
        e => {
            e.stopPropagation();
            const isVideo = FJAIToolUtil.getVideoAppTypeList().includes(appType);
            if (isVideo) {
                onAddToMedia && onAddToMedia(videoUrl);
            } else {
                onAddToMedia && onAddToMedia(imgUrl);
            }
        },
        [appType, onAddToMedia, videoUrl, imgUrl],
    );

    const _onAddToEditPage = useCallback(
        (e, callback) => {
            e.stopPropagation();
            onAddToEditPage && onAddToEditPage(id, callback);
        },
        [onAddToEditPage, id],
    );
    const _isShowPrivacyStatus = useMemo(() => {
        return (
            FJCommunityUtil.isUseNewPreviewModal(appType) &&
            ((isCreation && creationListLength > 1) || !isCreation) &&
            sourceStatus === FJConstant.SUCCESS &&
            isShowPrivacyStatus
        );
    }, [appType, creationListLength, isCreation, sourceStatus, isShowPrivacyStatus]);

    const _onPrivacySettingChange = (value, callback) => {
        onPrivacySettingChange && onPrivacySettingChange([item], value, callback);
    };

    return (
        <div
            className={classNames(styles.box, isShowSelected ? styles.selected : '')}
            onMouseEnter={() => setIsHover(true)}
            onMouseLeave={() => setIsHover(false)}
            onClick={_onClick}
        >
            {/* 资源预览 */}
            {videoUrl ? (
                <FJVideoPreviewItem
                    ref={videoPreviewItemRef}
                    videoUrl={videoUrl}
                    thumbnailUrl={imgUrl}
                    isLoading={sourceStatus === FJConstant.LOADING}
                    sourceLoadStatus={sourceLoadStatus}
                    setSourceLoadStatus={setSourceLoadStatus}
                    isHover={isHover}
                    isTransparentBG={isTransparentBG}
                />
            ) : (
                <FJImagePreviewItem
                    ref={imagePreviewItemRef}
                    imgUrl={imgUrl}
                    isLoading={sourceStatus === FJConstant.LOADING}
                    originalUrl={originalUrl}
                    sourceLoadStatus={sourceLoadStatus}
                    setSourceLoadStatus={setSourceLoadStatus}
                    setSourceOriginLoadStatus={setSourceOriginLoadStatus}
                    isTransparentBG={isTransparentBG}
                    isComparison={isComparison}
                />
            )}

            {/* 操作按钮 */}
            <FJSourcePreviewItemBtu
                loadStatus={sourceStatus}
                privacyStatus={privacyStatus}
                shareKey={shareKey}
                isShowPrivacyStatus={_isShowPrivacyStatus}
                sourceLoadStatus={sourceLoadStatus}
                sourceOriginLoadStatus={sourceOriginLoadStatus}
                isShowSelected={isShowSelected}
                propsIsHover={isHover}
                selected={selected}
                hideSelect={hideSelect}
                hideDelete={hideDelete}
                hideCompare={hideCompare}
                hideDownload={hideDownload || sourceStatus !== FJConstant.SUCCESS}
                hideEdit={hideEdit}
                onPrivacySettingChange={_onPrivacySettingChange}
                isShowAddToMediaButton={isShowAddToMediaButton && sourceStatus === FJConstant.SUCCESS}
                isApplyButton={isApplyButton}
                isShowAddToEditPageButton={isShowAddToEditPageButton && sourceStatus === FJConstant.SUCCESS}
                onAddToEditPage={_onAddToEditPage}
                onApplyMedia={isApplyButton ? _onApplyMedia : _onAddToMediaLibrary}
                onSelected={(bool, e) => {
                    onSelected && onSelected(id, bool, e);
                }}
                onDelete={() => {
                    onDelete && onDelete(id);
                }}
                onDownload={callback => {
                    onDownload && onDownload(id, callback);
                }}
                onEdit={callback => {
                    onEdit && onEdit(item, callback);
                }}
                isComparison={isComparison}
                setIsComparison={setIsComparison}
                onReloadOrigin={_onReloadOrigin}
            />

            {/* 状态 */}
            <FJSourceReviewItemStatus
                status={sourceStatus}
                data={statusData}
                onSourceReload={_onSourceReload}
                onRetry={_reGenerate}
                isVideoEffect={generationType === 'ev'}
                isRetryLoading={retryLoadingIds.includes(id)}
                appType={appType}
                isShowGenerating={isShowGenerating}
            />
        </div>
    );
});

FJSourcePreviewItem.propTypes = {
    item: PropTypes.object.isRequired,
    selected: PropTypes.bool,
    isShowSelected: PropTypes.bool,
    onSelected: PropTypes.func,
    onDelete: PropTypes.func,
    onDownload: PropTypes.func,
    onEdit: PropTypes.func,
    reGenerate: PropTypes.func,
    onClick: PropTypes.func,
    hideSelect: PropTypes.bool,
    hideDelete: PropTypes.bool,
    hideCompare: PropTypes.bool,
    hideDownload: PropTypes.bool,
    hideEdit: PropTypes.bool,
    appType: PropTypes.string,
    isTransparentBG: PropTypes.bool,
    isShowAddToMediaButton: PropTypes.bool,
    isApplyButton: PropTypes.bool,
    onApplyMedia: PropTypes.func,
    onAddToMedia: PropTypes.func,
    onReload: PropTypes.func,
    onAddToEditPage: PropTypes.func,
    isShowAddToEditPageButton: PropTypes.bool,
    retryLoadingIds: PropTypes.array,
    onPrivacySettingChange: PropTypes.func,
    isShowGenerating: PropTypes.bool,
    isShowPrivacyStatus: PropTypes.bool,
    creationListLength: PropTypes.number,
    isCreation: PropTypes.bool,
};

FJSourcePreviewItem.displayName = 'FJSourcePreviewItem';
export default FJSourcePreviewItem;
